/**
 * tf-supreme หมอกวนครอบฟ้า live (A5, SKELETON PASS, speed mode): simple versions of the tg* FX keys from the sample-style
 * kit (1-px dither dots on the scene grid, steel/white mist ramps, no glow, no beams): the turning mist ring, mist
 * spiralling in, swung-foe arcs, the ground ripple, palm prints in a rising dome, the dome sucked into the palms, the
 * ground pressure ring, settling rings, the fade and the whiff's scatter. Nothing here moves the caster; everything is
 * centred on it (the move is rooted). The rules' beats pick the key (`beats[i].fx`); times are world time of each beat.
 * Self-registered: one fx hook (event capture, as groundLand.ts) + one ground pass. TODO-polish: per-key art, the foe's
 * contact points, hand swirl, dust, proper dither levels, perf check.
 */
import { longMoveOf, type WorldEvent } from '@nine/shared';
import { registerRenderPass } from '../passes/registry';
import { C } from './sampleArt/families/swordKit';
import { registerFx, type FxHost } from './registry';

const ID = 'tf-supreme';
const R_RING = 3.2;

interface Run {
  x: number;
  y: number;
  tell: number;
  /** World time each fx key last started. */
  at: Map<string, number>;
  end: number | null;
  whiff: boolean;
}
const RUNS = new Map<number, Run>();

registerFx([], (host) => {
  let wrapped = false;
  return {
    step: () => {
      const w = host.world;
      for (const [id, r] of RUNS) if (r.end !== null && w.time - r.end > 2) RUNS.delete(id);
      if (wrapped) return;
      wrapped = true;
      const h = host as FxHost & { handle(ev: WorldEvent): void };
      const inner = h.handle.bind(h);
      h.handle = (ev: WorldEvent): void => {
        inner(ev);
        if (!ev.type.startsWith('long') || (ev as { skillId?: string }).skillId !== ID) return;
        const e = ev as Extract<WorldEvent, { skillId: string; src: number }>;
        const src = w.get(e.src);
        if (ev.type === 'longTell') {
          RUNS.set(e.src, { x: src?.x ?? ev.x, y: src?.y ?? ev.y, tell: w.time, at: new Map([['tgMistRing', w.time]]), end: null, whiff: false });
          return;
        }
        const run = RUNS.get(e.src);
        if (!run) return;
        if (ev.type === 'longBeat') {
          const fx = longMoveOf(ID)?.beats[ev.i]?.fx;
          if (fx) run.at.set(fx, w.time);
        } else if (ev.type === 'longEnd') {
          run.end = w.time;
          if (ev.outcome === 'whiff') {
            run.whiff = true;
            run.at.set('tgMistScatter', w.time);
          }
        }
      };
    },
  };
});

const hash = (i: number): number => {
  const s = Math.sin(i * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
};

registerRenderPass({
  id: 'longTaiji',
  layer: 'ground',
  order: 908,
  draw({ g, world: w, iso, ox, oy }) {
    if (!RUNS.size) return;
    const now = w.time;
    g.save();
    for (const run of RUNS.values()) {
      const t = (k: string): number => (run.at.has(k) ? now - run.at.get(k)! : -1);
      const dot = (wx: number, wy: number, up: number, col: string, a = 1): void => {
        const [x, y] = iso.proj(wx, wy);
        g.globalAlpha = a;
        g.fillStyle = col;
        g.fillRect(Math.round(x + ox), Math.round(y + oy - up), 1, 1);
      };
      /** Dots of an arc of radius r (world m) round the caster, on the ground or `up` px above it. */
      const arc = (r: number, a0: number, a1: number, up: number, ramp: string, tone: number, n: number, a = 1, sparse = 1): void => {
        for (let i = 0; i < n; i++) {
          if (sparse > 1 && i % sparse) continue;
          const an = a0 + ((a1 - a0) * i) / n;
          dot(run.x + Math.cos(an) * r, run.y + Math.sin(an) * r, up, C(ramp, tone + (i % 3 === 0 ? 1 : 0)), a);
        }
      };
      const clamp = (v: number): number => Math.max(0, Math.min(1, v));
      const fade = run.at.has('tgRingFade') ? clamp(1 - t('tgRingFade') / 0.6) : 1;
      // tgMistRing: the turning mist ring + a faint yin-yang disc (two half-discs of dither), kept until tgRingFade.
      const tr = t('tgMistRing');
      if (tr >= 0 && fade > 0 && !run.whiff) {
        const grow = clamp(tr / 0.8);
        const spin = tr * 1.6;
        arc(R_RING * grow, spin, spin + Math.PI * 2, 0, 'steel', 3, Math.round(R_RING * 60), 0.55 * fade);
        for (let q = 1; q <= 4; q++) arc((R_RING * q) / 5 * grow, -spin, -spin + Math.PI, 0, q % 2 ? 'white' : 'steel', 4, Math.round(q * 14), 0.2 * fade, 2);
      }
      // tgMistDraw: streams spiralling inward.
      const td = t('tgMistDraw');
      if (td >= 0 && td < 0.55) {
        for (let s = 0; s < 4; s++)
          for (let i = 0; i < 14; i++) {
            const u = clamp(td / 0.5 + i * 0.02 - 0.2);
            const rr = R_RING * (1 - u) * (0.4 + 0.6 * (i / 14));
            const an = (s * Math.PI) / 2 + u * 3.5 + i * 0.12;
            dot(run.x + Math.cos(an) * rr, run.y + Math.sin(an) * rr, 2 + i * 0.3, C('white', 5), 1 - u);
          }
      }
      // tgMistArcL / R: half-circle mist trail round the caster behind the swung foe.
      for (const [k, dir] of [['tgMistArcL', 1], ['tgMistArcR', -1]] as const) {
        const ta = t(k);
        if (ta >= 0 && ta < 0.6) {
          const head = clamp(ta / 0.45) * Math.PI;
          arc(1.7, dir > 0 ? 0 : Math.PI, dir > 0 ? head : Math.PI + head * -1, 8, 'white', 5, 40, 1 - ta / 0.6);
          arc(1.9, dir > 0 ? 0 : Math.PI, dir > 0 ? head * 0.9 : Math.PI - head * 0.9, 14, 'steel', 4, 30, (1 - ta / 0.6) * 0.5, 2);
        }
      }
      // tgMistFold: the ring slaps flat into a ripple under the kneeling foe.
      const tf = t('tgMistFold');
      if (tf >= 0 && tf < 0.7) {
        const u = clamp(tf / 0.5);
        arc(R_RING * (1 - u) + 1.2 * u, 0, Math.PI * 2, 0, 'white', 4, 70, 1 - clamp(tf / 0.7));
        arc(0.6 + u * 1.4, 0, Math.PI * 2, 0, 'steel', 3, 40, 0.7 * (1 - clamp(tf / 0.7)));
      }
      // tgPalmRain: eight solid palm prints in a dome that rises to head height.
      const tp = t('tgPalmRain');
      const gather = t('tgDomeGather');
      const burst = t('tgPressureWave');
      if (tp >= 0 && burst < 0) {
        const hDome = 40 * clamp(tp / 0.8) * (gather >= 0 ? 1 - clamp(gather / 0.6) : 1);
        const rDome = 1.9 * (gather >= 0 ? 1 - clamp(gather / 0.6) * 0.9 : 1);
        for (let q = 0; q < 5; q++) arc(rDome * Math.sin(((q + 1) / 6) * Math.PI * 0.5 + 0.5), now * 0.8 + q, now * 0.8 + q + Math.PI * 2, hDome * (q / 5), 'white', 4, 44, 0.35, 2);
        const prints = Math.min(8, Math.floor(tp / 0.11) + 1);
        if (gather < 0)
          for (let i = 0; i < prints; i++) {
            const an = hash(i) * Math.PI * 2;
            const rr = 0.5 + hash(i + 9) * 1.2;
            const up = 10 + hash(i + 3) * 24 * clamp(tp / 0.8);
            const age = tp - i * 0.11;
            const [x, y] = iso.proj(run.x + Math.cos(an) * rr, run.y + Math.sin(an) * rr);
            const px = Math.round(x + ox);
            const py = Math.round(y + oy - up);
            g.globalAlpha = age < 0.5 ? 1 : 0.5;
            g.fillStyle = C('white', 6);
            g.fillRect(px - 2, py - 2, 4, 4); // the palm
            g.fillRect(px - 2, py - 4, 1, 2);
            g.fillRect(px, py - 4, 1, 2);
            g.fillRect(px + 1, py - 4, 1, 2);
            g.fillStyle = C('steel', 2);
            g.fillRect(px - 3, py + 2, 6, 1); // outline stub
          }
      }
      // tgDomeGather: yin-yang swirl at the hands while the dome is sucked in.
      if (gather >= 0 && gather < 0.9) {
        const u = clamp(gather / 0.6);
        for (let i = 0; i < 20; i++) {
          const an = i * 0.55 + gather * 9;
          const rr = (0.9 * (1 - u) + 0.15) * (0.4 + (i % 5) / 5);
          dot(run.x + Math.cos(an) * rr + 0.25, run.y + Math.sin(an) * rr, 22 + (i % 5), C(i % 2 ? 'white' : 'ink', i % 2 ? 7 : 1), 1 - clamp((gather - 0.6) / 0.3));
        }
      }
      // tgPressureWave: a flat ring blasts out along the ground (reach 4 m), the dome bursts outward, dust.
      if (burst >= 0 && burst < 0.9) {
        const u = clamp(burst / 0.4);
        const a = 1 - clamp(burst / 0.9);
        arc(4 * u, 0, Math.PI * 2, 0, 'white', 5, 110, a);
        arc(4 * u * 0.92, 0, Math.PI * 2, 0, 'steel', 3, 90, a * 0.8, 2);
        arc(4 * u * 0.82, 0, Math.PI * 2, 0, 'steel', 4, 60, a * 0.4, 3);
        for (let q = 0; q < 4; q++) arc(2 * u + q * 0.3, 0, Math.PI * 2, 6 + q * 8 * u, 'white', 4, 40, a * 0.5, 3);
      }
      // tgMistSettle: the burst mist rains down in rings.
      const ts = t('tgMistSettle');
      if (ts >= 0 && ts < 1.2) {
        for (let q = 0; q < 4; q++) {
          const u = clamp((ts - q * 0.12) / 0.8);
          if (u <= 0) continue;
          arc(2 + q * 0.5, 0, Math.PI * 2, 30 * (1 - u), 'white', 3, 50, (1 - u) * 0.6, 2);
        }
      }
      // tgMistScatter: the whiff, the ring breaks up and drifts.
      const tw = t('tgMistScatter');
      if (tw >= 0 && tw < 1.2) {
        const a = 1 - clamp(tw / 1.2);
        for (let i = 0; i < 70; i++) {
          const an = (i / 70) * Math.PI * 2;
          const d = R_RING + hash(i) * tw * 1.2;
          dot(run.x + Math.cos(an + tw * 0.3) * d, run.y + Math.sin(an + tw * 0.3) * d, tw * 8 * hash(i + 4), C('steel', 3), a * 0.6);
        }
      }
    }
    g.restore();
  },
});
